import { DEMO_BANK_QUESTION } from './demoQuestionBank';
import { createDemoMathPracticeAdapter } from './demoMathPracticeAdapter';
import { demoLearningNotes, demoLearningMap, DEMO_WORKBOOK_ID } from './demoLearningOutcomes';
import { STUDY_DEMO_TUTOR_EXAMPLES, DEMO_TUTOR_NOTICE } from './demoTutorExamples';
import {
  STUDY_DEMO_LEARNER,
  STUDY_DEMO_CASES,
  demoWorkingRows,
  demoDocuments,
} from './exampleData';
import type { StudyPdfAnnotation } from '@/pages/learn/study/studyApi';
import type { StudyDocumentPageImage } from '@/pages/learn/study/studyAnalysisApi';
import type { StudyDocumentDetail } from '@/pages/learn/study/studyApi';
import { assertSupportedStudyFile } from '@/pages/learn/study/studyUploadTypes';
import { workingContentSchema, type WorkingRow } from '@/pages/learn/study/workingCanvas';
import type { AcademyQuestion } from '@/pages/learn/academy/academyApi';
import { ApiError, type ApiOpts, type DemoApiAdapter } from '@/lib/api';
import type { StudyHome, StudyTask, StudyAssessment } from '@/pages/learn/study/studyApi';
import type { StudyNote } from '@/pages/learn/study/studyNotesApi';
import type { StudyMindMap, StudyMindMapContent } from '@/pages/learn/study/studyMindMapApi';
import { contentPreview } from '@/pages/learn/study/studyMindMapModel';
import { createSampleMaterialExperience } from './sampleMaterialExperience';
import { SAMPLE_PROFILE } from './sampleProfile';
import { SAMPLE_REFERENCE } from './sampleReference';
import { createSampleScienceExplanations } from './sampleScienceExplanations';

const clone = <T>(value: T): T => structuredClone(value);
const fields = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown, fallback = '') => (typeof value === 'string' ? value : fallback);
const subjectRows = [
  ['mathematics', 'Mathematics'],
  ['chemistry', 'Chemistry'],
  ['physics', 'Physics'],
  ['biology', 'Biology'],
  ['science', 'Science'],
];
const blocked = () =>
  new ApiError(
    403,
    'STUDY_EXPERIENCE_ONLY',
    'This action is unavailable in the student experience. No data was sent.',
  );

/** A per-document sandbox behind the existing demo API boundary. There is no network fallback. */
export function createStudyExperienceAdapter(year = 8): DemoApiAdapter {
  const exampleYear = Number.isInteger(year) && year >= 6 && year <= 12 ? year : 8;
  const now = () => new Date().toISOString();
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const term = {
    id: 'example-term',
    term_number: 4,
    label: 'Term 4',
    starts_on: null,
    ends_on: null,
    date_provenance: 'unknown' as const,
    status: 'active' as const,
    is_current: true,
    week: null,
  };
  let subjects = subjectRows.map(([subject_key, display_name], index) => ({
    id: `example-subject-${index}`,
    subject_key,
    display_name,
  }));
  const home: StudyHome = {
    setup_status: 'ready',
    school: { name: STUDY_DEMO_LEARNER.school, suburb: null, state: 'QLD', acara_id: null },
    academic_year: {
      id: 'example-year',
      school_year: Number(date.slice(0, 4)),
      year_level: exampleYear,
      jurisdiction: 'QLD',
      timezone: 'Australia/Brisbane',
    },
    current_term: term,
    academic_years: [],
    terms: [term],
    subjects,
    today: [],
    upcoming_assessments: [],
    review_due_count: 0,
  };
  home.academic_years = [
    { ...home.academic_year!, status: 'active', is_current: true, terms: [{ ...term, subjects }] },
  ];
  const tasks: StudyTask[] = [
    {
      id: 'example-task',
      title: 'Compare the cone and sphere results in my workbook',
      source_url: null,
      status: 'today',
      due_on: date,
      term: { id: term.id, label: term.label },
      subject: subjects[0],
      completed_at: null,
      created_at: now(),
    },
  ];
  tasks.push({
    id: 'example-task-completed',
    title: 'Check both roots and explain the discriminant',
    source_url:
      '/learn/study/working/' +
      STUDY_DEMO_CASES.find((item) => item.key === 'quadratic')!.workingId,
    status: 'done',
    due_on: '2026-10-16',
    term: { id: term.id, label: term.label },
    subject: subjects[0],
    completed_at: '2026-10-16T06:00:00.000Z',
    created_at: '2026-10-15T03:00:00.000Z',
  });
  const notes: StudyNote[] = demoLearningNotes();
  const assessments: StudyAssessment[] = [
    {
      id: 'example-maths-check-in',
      title: 'Mathematics practice check-in (fictional)',
      assessment_type: 'quiz',
      scope:
        'Explain ratios, cone height and exact algebra checks. This is a prepared example schedule, not a marked assessment.',
      due_on: '2026-10-22',
      ai_help_policy: 'allowed',
      status: 'upcoming',
      completed_at: null,
      term_id: term.id,
      term: { term_number: 4, label: term.label },
      subject: { subject_key: 'mathematics', display_name: 'Mathematics' },
      created_at: '2026-10-16T03:00:00.000Z',
    },
  ];
  const documents: StudyDocumentDetail[] = demoDocuments().sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );
  const annotations = new Map<string, Array<StudyPdfAnnotation & { request_id: string }>>();
  const documentPages = new Map<string, StudyDocumentPageImage>();
  const favourites: Array<{ subject_key: string; tool_key: string }> = [];
  const working = new Map<string, WorkingRow & { updated_at: string }>(
    demoWorkingRows().map((row) => [row.id, row]),
  );
  const material = createSampleMaterialExperience({
    documents,
    notes,
    working,
    subject: subjects[0],
    termId: term.id,
    date,
  });
  const sciences = createSampleScienceExplanations(documents, term.id, date);
  tasks.push({
    ...tasks[0],
    id: 'example-task-done',
    title: 'Read the fractions source and save notes',
    status: 'done',
    completed_at: tasks[0].created_at,
  });
  assessments.push({
    id: 'example-assessment',
    title: 'Fractions explanation check',
    assessment_type: 'quiz',
    scope: 'Equal groups, equivalent fractions and fractions of quantities',
    due_on: date,
    ai_help_policy: 'not_allowed',
    status: 'upcoming',
    completed_at: null,
    term_id: term.id,
    term: { term_number: 4, label: term.label },
    subject: subjects[0],
    created_at: now(),
  });
  let portfolio = {
    version: 1,
    narrative:
      'Prepared fictional reflection: I am practising Year 10 exact values and checking equations. I also revisit earlier fractions and science foundations. These examples do not show mastery.',
    provenance: 'learner_confirmed',
    highlights: STUDY_DEMO_CASES.slice(0, 3).map((item) => ({
      key: `working:${item.workingId}`,
      title: item.title,
      href: `/learn/study/working/${item.workingId}`,
      available: true,
    })) as Array<{
      key: string;
      title: string;
      href: string | null;
      available: boolean;
    }>,
  };
  let wrongPractice = true;
  let practiceAttempts = 1;
  const question: AcademyQuestion = {
    id: DEMO_BANK_QUESTION.id,
    source_ref: 'Original experience example',
    exam: 'Example practice',
    subject: 'Mathematics',
    year_level: `Year ${exampleYear}`,
    variant: 'example',
    paper_year: Number(date.slice(0, 4)),
    q_no: 1,
    answer_type: 'choice',
    marks: 1,
    section: null,
    stimulus_id: null,
    stem_text: DEMO_BANK_QUESTION.stem,
    options: DEMO_BANK_QUESTION.options,
    figure_keys: [],
    render_ready: true,
    render_spec: { kind: 'none' },
    ac9_code: null,
    difficulty: null,
  };
  const maps: StudyMindMap[] = [demoLearningMap()];
  const preparedMath = createDemoMathPracticeAdapter();
  const workbook = documents.find((row) => row.id === DEMO_WORKBOOK_ID)!;
  workbook.analysis = {
    status: 'succeeded',
    topic: preparedMath.analysis.result!.topic.label,
    questions_total: 0,
    questions_done: 0,
  };
  const mapFrom = (input: Record<string, unknown>): StudyMindMap => {
    const content =
      input.content && typeof input.content === 'object'
        ? clone(input.content as StudyMindMapContent)
        : { nodes: [], edges: [], viewport: { x: 0, y: 0, zoom: 1 } };
    return {
      id: crypto.randomUUID(),
      subject_key: text(input.subject_key, 'mathematics'),
      term_id: term.id,
      title: text(input.title, 'Untitled map'),
      version: 1,
      node_count: content.nodes.length,
      edge_count: content.edges.length,
      source_document_ids: [],
      content,
      limits: { nodes_per_map: 300, edges_per_map: 600 },
      created_at: now(),
      updated_at: now(),
    };
  };
  const rowSubject = (key: unknown) => subjects.find((row) => row.subject_key === key) ?? null;
  const requireRow = <T>(value: T | undefined): T => {
    if (!value) throw new ApiError(404, 'EXAMPLE_NOT_FOUND', 'This example is not available.');
    return value;
  };
  const cap = (rows: unknown[]) => {
    if (rows.length >= 50)
      throw new ApiError(
        409,
        'EXPERIENCE_LIMIT',
        'Reset the experience to start with fresh example data.',
      );
  };
  const availability = {
    study_visible: true,
    hsc_plan_visible: false,
    learner_source: 'institution',
    year_level: exampleYear,
    policy: {
      institution_id: null,
      uploads_enabled: true,
      disabled_tool_keys: [],
      subjects: [],
      default_subject: {
        enabled: true,
        tools_enabled: true,
        practice_enabled: true,
        generated_practice_enabled: false,
        tutor: {
          state: 'disabled_institution',
          max_hint_level: 0,
          resumes_at: null,
          exam_label: null,
        },
      },
    },
  };

  function handle(path: string, opts: ApiOpts): unknown {
    const url = new URL(path, 'https://experience.invalid');
    const p = url.pathname;
    const method = opts.method ?? 'GET';
    const body = fields(opts.body);
    const pageUpload = /^\/study\/experience\/documents\/([^/]+)\/pages\/(\d+)$/.exec(p);
    const pageRead = /^\/study\/documents\/([^/]+)\/pages\/(\d+)$/.exec(p);
    if (pageUpload && method === 'POST') {
      const doc = requireRow(documents.find((row) => row.id === pageUpload[1]));
      const page = Number(pageUpload[2]);
      if (!(body.image instanceof Blob) || page < 1 || page > 10) throw blocked();
      const key = `${doc.id}:${page}`;
      if (!documentPages.has(key))
        documentPages.set(key, {
          document_id: doc.id,
          page_number: page,
          mime_type: 'image/png',
          size_bytes: body.image.size,
          provenance: doc.mime_type === 'application/pdf' ? 'client_pdfjs' : 'client_photo',
          read_url: URL.createObjectURL(body.image),
          read_url_expires_in: 3600,
        });
      return documentPages.get(key);
    }
    if (pageRead && method === 'GET' && pageRead[1] === DEMO_WORKBOOK_ID && pageRead[2] === '1')
      return {
        document_id: DEMO_WORKBOOK_ID,
        page_number: 1,
        mime_type: 'image/png',
        size_bytes: 0,
        provenance: 'client_pdfjs',
        read_url: '/study-demo/alex-workbook-page-1.png',
        read_url_expires_in: 3600,
      };
    if (pageRead && method === 'GET')
      return requireRow(documentPages.get(`${pageRead[1]}:${pageRead[2]}`));
    if (p === '/study/experience/documents' && method === 'POST') {
      if (!(body.file instanceof File)) throw blocked();
      assertSupportedStudyFile(body.file);
      if (body.file.size > 30 * 1024 * 1024)
        throw new ApiError(400, 'EXPERIENCE_FILE_SIZE', 'Use a file smaller than 30 MB.');
      cap(documents);
      const read_url = URL.createObjectURL(body.file);
      const doc: StudyDocumentDetail = {
        id: crypto.randomUUID(),
        file_name: body.file.name,
        display_name: null,
        mime_type: body.file.type,
        size_bytes: body.file.size,
        status: 'uploaded',
        classification_state: 'learner_confirmed',
        context_suggestion: null,
        term_id: term.id,
        subject: rowSubject(body.subject_key),
        created_at: now(),
        read_url,
        read_url_expires_in: 3600,
        source_revision: 'experience-1',
        thumbnail: {
          kind: body.file.type === 'application/pdf' ? 'pdf' : 'image',
          url: read_url,
          expires_in: 3600,
        },
        analysis: null,
      };
      documents.unshift(doc);
      return doc;
    }
    const documentExtra =
      /^\/study\/documents\/([^/]+)\/(analysis|transcript|image-annotations|pdf-annotations)(?:\/([^/]+))?$/.exec(
        p,
      );
    if (documentExtra) {
      const doc = requireRow(documents.find((row) => row.id === documentExtra[1]));
      if (['analysis', 'transcript'].includes(documentExtra[2]) && method === 'GET')
        return doc.id === DEMO_WORKBOOK_ID && documentExtra[2] === 'analysis'
          ? preparedMath.analysis
          : null;
      if (documentExtra[2].endsWith('annotations')) {
        const rows = annotations.get(doc.id) ?? [];
        annotations.set(doc.id, rows);
        if (method === 'GET') return { items: rows, next_cursor: null };
        if (method === 'POST') {
          const same = rows.find((row) => row.request_id === body.request_id);
          if (same) return same;
          const row: StudyPdfAnnotation & { request_id: string } = {
            id: crypto.randomUUID(),
            request_id: text(body.request_id),
            source_revision: doc.source_revision,
            page_number: Number(body.page_number) || 1,
            kind: body.kind as StudyPdfAnnotation['kind'],
            geometry: fields(body.geometry),
            body: text(body.body) || null,
            version: 1,
            created_at: now(),
            updated_at: now(),
          };
          rows.push(row);
          return row;
        }
        if (method === 'DELETE' && documentExtra[3]) {
          const i = rows.findIndex((row) => row.id === documentExtra[3]);
          if (i >= 0) rows.splice(i, 1);
          return { id: documentExtra[3], status: 'deleted' };
        }
      }
    }
    const documentMatch = /^\/study\/documents\/([^/]+)(?:\/(name))?$/.exec(p);
    if (documentMatch && p !== '/study/documents/page' && ['GET', 'PATCH'].includes(method)) {
      const doc = requireRow(
        documents.find((item) => item.id === decodeURIComponent(documentMatch[1])),
      );
      if (method === 'GET') return doc;
      if (method === 'PATCH') {
        if (documentMatch[2] === 'name') doc.display_name = text(body.display_name) || null;
        else {
          doc.subject = rowSubject(body.subject_key);
          doc.term_id = text(body.term_id, term.id);
        }
        return doc;
      }
    }
    if (p === '/study/cheatsheets' && method === 'GET') return { sheets: [SAMPLE_REFERENCE] };
    if (p === '/study/home' && method === 'GET')
      return {
        ...home,
        subjects,
        today: tasks.filter((row) => row.status === 'today'),
        upcoming_assessments: assessments,
        review_due_count: [...material.reviews, ...preparedMath.reviews].filter((row) => row.due)
          .length,
      };
    if (p === '/study/me/availability' && method === 'GET')
      return { ...availability, year_level: home.academic_year?.year_level };
    if (p === '/study/tools/access' && method === 'GET')
      return { default_interactive_tools: true, subjects: [] };
    if (p === '/study/tools/favourites' && method === 'GET') return favourites;
    const favouriteMatch = /^\/study\/tools\/favourites\/([^/]+)\/([^/]+)$/.exec(p);
    if (favouriteMatch && (method === 'PUT' || method === 'DELETE')) {
      const subject_key = decodeURIComponent(favouriteMatch[1]);
      const tool_key = decodeURIComponent(favouriteMatch[2]);
      const found = favourites.findIndex(
        (row) => row.subject_key === subject_key && row.tool_key === tool_key,
      );
      if (found >= 0) favourites.splice(found, 1);
      if (method === 'PUT') favourites.push({ subject_key, tool_key });
      return { subject_key, tool_key, favourite: method === 'PUT' };
    }
    if (p === '/study/tool-registry' && method === 'GET') return { tools: [] };
    if (p === '/study/me/setup' && method === 'PUT') {
      const selected = Array.isArray(body.subject_keys) ? body.subject_keys : [];
      subjects = subjectRows
        .filter(([key]) => selected.includes(key))
        .map(([subject_key, display_name], i) => ({
          id: `example-subject-${i}`,
          subject_key,
          display_name,
        }));
      if (home.academic_year) home.academic_year.year_level = Number(body.year_level) || 8;
      return { ...home, subjects };
    }
    if (p === '/study/notes' && method === 'GET')
      return {
        items: notes.filter(
          (note) =>
            (!url.searchParams.get('document_id') ||
              note.pages.some(
                (page) => page.document_id === url.searchParams.get('document_id'),
              )) &&
            note.status === (url.searchParams.get('status') ?? 'active') &&
            (!url.searchParams.get('search') ||
              `${note.title} ${note.body}`
                .toLowerCase()
                .includes(url.searchParams.get('search')!.toLowerCase())) &&
            (!url.searchParams.get('subject_key') ||
              note.subject_key === url.searchParams.get('subject_key')),
        ),
        next_cursor: null,
      };
    if (p === '/study/notes' && method === 'POST') {
      const found = notes.find((note) => note.id === body.id);
      if (found && body.expected_version !== found.version)
        throw new ApiError(409, 'NOTE_VERSION_CONFLICT', 'Reopen the notes and try again.');
      if (!found) cap(notes);
      const note: StudyNote = {
        id: text(body.id, crypto.randomUUID()),
        title: text(body.title),
        body: text(body.body),
        topic: text(body.topic) || null,
        subject_key: text(body.subject_key) || null,
        term_id: text(body.term_id) || null,
        class_date: text(body.class_date) || null,
        pages: Array.isArray(body.pages)
          ? body.pages
              .map((value) => {
                const page = fields(value);
                return {
                  document_id: text(page.document_id),
                  rotation: ([0, 90, 180, 270].includes(Number(page.rotation))
                    ? Number(page.rotation)
                    : 0) as 0 | 90 | 180 | 270,
                };
              })
              .filter((page) => documents.some((doc) => doc.id === page.document_id))
          : [],
        version: (found?.version ?? 0) + 1,
        status: body.status === 'archived' ? 'archived' : 'active',
        available: true,
        updated_at: now(),
        reviews: found?.reviews ?? [],
      };
      if (body.status === 'deleted') {
        if (found) notes.splice(notes.indexOf(found), 1);
      } else if (found) Object.assign(found, note);
      else notes.unshift(note);
      return { id: note.id, version: note.version };
    }
    const noteMatch = /^\/study\/notes\/([^/]+)(?:\/(reviews|maps))?$/.exec(p);
    if (noteMatch) {
      const note = requireRow(notes.find((row) => row.id === decodeURIComponent(noteMatch[1])));
      if (!noteMatch[2] && method === 'GET') return note;
      if (noteMatch[2] === 'maps' && method === 'GET')
        return { eligible: [], linked: [], truncated: false, unavailable_links: 0 };
      if (noteMatch[2] === 'reviews' && method === 'POST') {
        cap(tasks);
        const task: StudyTask = {
          id: crypto.randomUUID(),
          title: text(body.title, `Review ${note.title}`),
          status: 'later',
          source_url: null,
          due_on: text(body.due_on) || null,
          term: { id: term.id, label: term.label },
          subject: rowSubject(note.subject_key),
          completed_at: null,
          created_at: now(),
        };
        tasks.unshift(task);
        note.reviews ??= [];
        note.reviews.push({
          task_id: task.id,
          title: task.title,
          due_on: task.due_on,
          term_id: term.id,
          notebook_version: note.version,
          status: task.status,
        });
        return { task_id: task.id };
      }
    }
    if (p === '/study/tasks' && method === 'GET') return tasks;
    if (p === '/study/tasks/page' && method === 'GET') return { items: tasks, next_cursor: null };
    if (p === '/study/tasks' && method === 'POST') {
      cap(tasks);
      const task: StudyTask = {
        id: crypto.randomUUID(),
        title: text(body.title),
        status: 'inbox',
        source_url: text(body.source_url) || null,
        due_on: text(body.due_on) || null,
        term: { id: term.id, label: term.label },
        subject: rowSubject(body.subject_key),
        completed_at: null,
        created_at: now(),
      };
      tasks.unshift(task);
      return task;
    }
    const taskMatch = /^\/study\/tasks\/([^/]+)$/.exec(p);
    if (taskMatch) {
      const task = requireRow(tasks.find((row) => row.id === decodeURIComponent(taskMatch[1])));
      if (method === 'PATCH') {
        Object.assign(task, body, { id: task.id });
        if (body.status === 'done') task.completed_at = now();
      }
      if (method === 'GET' || method === 'PATCH') return task;
    }
    if (p === '/study/mind-maps' && method === 'GET')
      return {
        subject_key: url.searchParams.get('subject_key'),
        items: maps
          .filter((row) => row.subject_key === url.searchParams.get('subject_key'))
          .map((row) => ({
            ...row,
            source_document_count: 0,
            preview: contentPreview(row.content),
          })),
        limits: { maps_per_subject: 50, nodes_per_map: 300 },
      };
    if (p === '/study/mind-maps' && method === 'POST') {
      cap(maps);
      const map = mapFrom(body);
      maps.unshift(map);
      return map;
    }
    const mapMatch = /^\/study\/mind-maps\/([^/]+)(?:\/(revisions|duplicate))?$/.exec(p);
    if (mapMatch) {
      const map = requireRow(maps.find((row) => row.id === decodeURIComponent(mapMatch[1])));
      if (method === 'GET')
        return mapMatch[2] === 'revisions' ? { items: [], next_cursor: null } : map;
      if (method === 'DELETE') {
        maps.splice(maps.indexOf(map), 1);
        return { id: map.id, deleted: true };
      }
      if (mapMatch[2] === 'duplicate' && method === 'POST') {
        cap(maps);
        const copy = mapFrom(map as unknown as Record<string, unknown>);
        copy.title += ' (copy)';
        maps.unshift(copy);
        return copy;
      }
      if (method === 'PUT' || method === 'PATCH') {
        if (method === 'PUT' && body.expected_version !== map.version)
          throw new ApiError(409, 'STUDY_MIND_MAP_VERSION_CONFLICT', 'Reopen this mind map.');
        Object.assign(map, body, { id: map.id, version: map.version + 1, updated_at: now() });
        map.node_count = map.content.nodes.length;
        map.edge_count = map.content.edges.length;
        return map;
      }
    }
    const assessmentMatch = /^\/study\/assessments\/([^/]+)$/.exec(p);
    if (assessmentMatch) {
      const assessment = requireRow(assessments.find((row) => row.id === assessmentMatch[1]));
      if (method === 'PATCH') {
        Object.assign(assessment, body, { id: assessment.id });
        if (body.status === 'completed') assessment.completed_at = now();
      }
      if (method === 'GET' || method === 'PATCH') return assessment;
    }
    if (p === '/study/assessments' && method === 'GET') return assessments;
    if (p === '/study/assessments' && method === 'POST') {
      cap(assessments);
      const assessment: StudyAssessment = {
        id: crypto.randomUUID(),
        title: text(body.title),
        assessment_type: [
          'assignment',
          'quiz',
          'test',
          'exam',
          'project',
          'oral',
          'practical',
          'other',
        ].includes(text(body.assessment_type))
          ? (body.assessment_type as StudyAssessment['assessment_type'])
          : 'other',
        scope: text(body.scope) || null,
        due_on: text(body.due_on, date),
        ai_help_policy: ['allowed', 'limited', 'not_allowed'].includes(text(body.ai_help_policy))
          ? (body.ai_help_policy as StudyAssessment['ai_help_policy'])
          : 'unknown',
        term_id: term.id,
        term: { term_number: 4, label: term.label },
        subject: requireRow(rowSubject(body.subject_key) ?? undefined),
        status: 'upcoming',
        completed_at: null,
        created_at: now(),
      };
      assessments.unshift(assessment);
      return assessment;
    }
    if (/^\/study\/subjects\/[^/]+\/tutor$/.test(p) && method === 'GET') {
      const key = p.split('/')[3];
      return {
        subject_key: key,
        subject_name: rowSubject(key)?.display_name ?? key,
        tutor_name: 'Tuan Tuan',
        stars_per_message: null,
        thread: key === 'mathematics' ? STUDY_DEMO_TUTOR_EXAMPLES[0].thread : null,
        suggested_questions: {},
        availability: {
          state: 'account_unavailable',
          message: DEMO_TUTOR_NOTICE,
          policy_state: null,
          resumes_at: null,
          exam_label: null,
          page_kinds: {},
          non_ai: { materials: true, practice: true, tools: true },
        },
      };
    }
    if (/^\/study\/subjects\/[^/]+\/tutor\/threads$/.test(p) && method === 'GET')
      return {
        subject_key: p.split('/')[3],
        threads:
          p.split('/')[3] === 'mathematics'
            ? STUDY_DEMO_TUTOR_EXAMPLES.map((row) => row.thread)
            : [],
      };
    const demoThread = /^\/study\/tutor\/threads\/([^/]+)$/.exec(p);
    if (demoThread && method === 'GET') {
      const example = STUDY_DEMO_TUTOR_EXAMPLES.find(
        (row) => row.thread.id === decodeURIComponent(demoThread[1]),
      );
      if (!example)
        throw new ApiError(404, 'DEMO_THREAD_NOT_FOUND', 'This prepared example was not found.');
      return { thread: example.thread, turns: example.turns };
    }
    if (p === '/study/assignments' && method === 'GET')
      return {
        visibility_notice: { version: 'example', text: 'Example classroom work.' },
        assignments: [],
      };
    if (p === '/study/working-canvases' && method === 'GET')
      return {
        items: [...working.values()]
          .filter(
            (row) =>
              !url.searchParams.get('kind') || row.context.kind === url.searchParams.get('kind'),
          )
          .sort((a, b) => b.updated_at.localeCompare(a.updated_at))
          .map((row) => ({
            ...row,
            title: row.content.question?.trim().slice(0, 80) || 'My working',
            question: row.content.question ?? null,
            retention_until: null,
            stroke_count: row.content.strokes.length,
            byte_size: JSON.stringify(row.content).length,
          })),
        next_cursor: null,
      };
    if (p === '/study/working-canvases/draft' && method === 'GET')
      return (
        [...working.values()].find(
          (row) =>
            row.context.question_id === url.searchParams.get('question_id') &&
            row.context.kind === url.searchParams.get('kind'),
        ) ?? null
      );
    const workingMatch = /^\/study\/working-canvases\/([^/]+)$/.exec(p);
    if (workingMatch) {
      const id = decodeURIComponent(workingMatch[1]);
      const row = working.get(id);
      if (method === 'GET')
        return {
          ...requireRow(row),
          title: row?.content.question || 'My working',
          question: row?.content.question ?? null,
          retention_until: null,
          stroke_count: row?.content.strokes.length ?? 0,
          byte_size: JSON.stringify(row?.content).length,
        };
      if (method === 'PUT') {
        if ((row?.version ?? 0) !== body.expected_version)
          throw new ApiError(409, 'WORKING_CANVAS_VERSION_CHANGED', 'Reopen this working page.');
        const context = fields(body.context);
        if (!['free', 'practice', 'study', 'mock', 'material'].includes(text(context.kind)))
          throw blocked();
        if (!row && working.size >= 50)
          throw new ApiError(409, 'EXPERIENCE_LIMIT', 'Reset the experience to start again.');
        const next = {
          id,
          content: workingContentSchema.parse(body.content),
          version: (row?.version ?? 0) + 1,
          context: {
            kind: context.kind as WorkingRow['context']['kind'],
            question_id: text(context.question_id),
          },
          locked: false,
          updated_at: now(),
        };
        working.set(id, next);
        return next;
      }
      if (method === 'DELETE') {
        working.delete(id);
        return { id, deleted: true };
      }
    }
    if (p === '/study/analyses/unseen' && method === 'GET') return { items: [] };
    if ((p === '/study/documents' || p === '/study/documents/page') && method === 'GET') {
      const found = documents.filter(
        (doc) =>
          (!url.searchParams.get('term_id') ||
            (url.searchParams.get('term_id') === 'inbox'
              ? !doc.term_id
              : doc.term_id === url.searchParams.get('term_id'))) &&
          (!url.searchParams.get('subject_key') ||
            doc.subject?.subject_key === url.searchParams.get('subject_key')) &&
          (!url.searchParams.get('search') ||
            doc.file_name.toLowerCase().includes(url.searchParams.get('search')!.toLowerCase())),
      );
      return p.endsWith('/page') || url.search ? { items: found, next_cursor: null } : found;
    }
    if (p === '/study/practice/questions' || p === '/study/practice/mistakes') {
      if (method === 'GET')
        return {
          context: {
            subject_key: url.searchParams.get('subject_key'),
            year_level: url.searchParams.get('year_level'),
            alignment: 'learner_selected',
          },
          questions:
            url.searchParams.get('subject_key') === 'mathematics' &&
            (p.endsWith('/questions') || wrongPractice)
              ? [question]
              : [],
          coverage_gap: url.searchParams.get('subject_key') !== 'mathematics',
          mistake_count: wrongPractice ? 1 : 0,
        };
    }
    if (p === '/study/practice/attempts' && method === 'POST' && body.question_id === question.id) {
      practiceAttempts++;
      const correct = body.submitted === DEMO_BANK_QUESTION.answer;
      wrongPractice = !correct;
      return {
        is_correct: correct,
        correct_answer: DEMO_BANK_QUESTION.answer,
        attempt_number: practiceAttempts,
        can_retry: !correct,
        mistake_resolved: correct,
        transfer_validated: false,
        transfer_question: null,
        review_completed: false,
        review_due_at: null,
        review_interval_days: null,
        micro_hint: correct ? null : DEMO_BANK_QUESTION.hint,
      };
    }
    if (p === '/study/me/journey' && method === 'GET')
      return {
        academic_year: { ...home.academic_year, timezone: 'Australia/Brisbane', is_current: true },
        terms: [{ ...term, subjects }],
        items: [
          ...sciences.history.map((item) => ({
            ...item,
            key: item.id,
            kind: 'task',
            term_id: term.id,
            status: 'completed',
            title: `Prepared history · ${item.title}`,
          })),
          ...material.history.map((item) => ({
            ...item,
            kind: 'task',
            title: `${item.evidence.startsWith('Prepared') ? 'Prepared history' : 'Your demo action'} · ${item.title}`,
            key: item.id,
            term_id: term.id,
            subject_key: 'mathematics',
            status: 'completed',
          })),
          ...tasks.map((task) => ({
            key: `task:${task.id}`,
            id: task.id,
            kind: 'task',
            title: task.title,
            term_id: term.id,
            subject_key: task.subject?.subject_key ?? null,
            status: task.status,
            at: task.completed_at ?? task.created_at,
            href: `/learn/study?task=${task.id}`,
            evidence: 'Fictional learning task - not assessed',
          })),
          ...[...working.values()].map((row) => ({
            key: `working:${row.id}`,
            id: row.id,
            kind: 'working',
            title: row.content.question ?? 'My working',
            term_id: term.id,
            subject_key: 'mathematics',
            status: 'saved',
            at: row.updated_at,
            href: `/learn/study/working/${row.id}`,
            evidence: 'Prepared example; editable, not assessed',
          })),
        ].sort((a, b) => b.at.localeCompare(a.at)),
        next_cursor: null,
        mastery_status: 'not_assessed',
      };
    if (p === '/study/me/journey/portfolio' && method === 'GET') return portfolio;
    if (p === '/study/me/journey/portfolio' && method === 'POST') {
      if (body.expected_version !== portfolio.version)
        throw new ApiError(409, 'STORY_VERSION_CHANGED', 'Reopen your story before saving.');
      const available = new Map<
        string,
        { key: string; title: string; href: string | null; available: boolean }
      >([
        ...[...working.values()].map(
          (row) =>
            [
              `working:${row.id}`,
              {
                key: `working:${row.id}`,
                title: row.content.question || 'My working',
                href: `/learn/study/working/${row.id}`,
                available: true,
              },
            ] as const,
        ),
        ...tasks.map(
          (task) =>
            [
              `task:${task.id}`,
              {
                key: `task:${task.id}`,
                title: task.title,
                href: `/learn/study?task=${task.id}`,
                available: true,
              },
            ] as const,
        ),
      ]);
      const highlights = [
        ...new Set(
          Array.isArray(body.highlights) ? body.highlights.map((value) => text(value)) : [],
        ),
      ]
        .slice(0, 20)
        .flatMap((key) => {
          const row = available.get(key);
          return row ? [row] : [];
        });
      portfolio = {
        ...portfolio,
        narrative: text(body.narrative).slice(0, 4000),
        highlights,
        version: portfolio.version + 1,
      };
      return { version: portfolio.version };
    }
    if (
      method === 'GET' &&
      [
        '/study/english-learning/reviews',
        '/study/tools/chemical-equation/review',
        '/study/links',
        '/study/privacy/deletions',
      ].includes(p)
    )
      return [];
    if (p === '/study/english-learning/sharing' && method === 'GET')
      return { limit: 0, shares: [] };
    if (p === '/study/me/export' && method === 'GET')
      return {
        example: true,
        notice: 'Fictional Alex workspace only. No real learner, account or cloud records.',
        profile: SAMPLE_PROFILE,
        notes,
        tasks,
        maps,
        working: [...working.values()],
        history: [...material.history, ...sciences.history],
      };
    throw blocked();
  }
  return async <T>(path: string, opts: ApiOpts = {}) => {
    const url = new URL(path, 'https://experience.invalid');
    if (url.pathname === '/study/review' && (opts.method ?? 'GET') === 'GET')
      return clone([...material.reviews, ...preparedMath.reviews]) as T;
    const prepared = preparedMath.handle(path, opts);
    if (prepared) return clone(prepared.value) as T;
    const science = await sciences.handle(path, opts);
    if (science) return clone(science.value) as T;
    const sample = await material.handle(path, opts);
    return clone(sample ? sample.value : handle(path, opts)) as T;
  };
}
